import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, Loader2, CheckCircle2, ExternalLink, ShieldCheck } from "lucide-react";

import Sidebar   from "../components/Company/Includes/Sidebar";
import TopBar    from "../components/Company/Includes/TopBar";
import BottomNav from "../components/Company/Includes/BottomNav";

// Same design tokens as PostLoadPage/Analytics/MyLoads — shares the
// "pl_theme" localStorage key so this page matches whichever mode the
// rest of the company dashboard is in.
const T = {
  bg:"var(--ao-bg)", surface:"var(--ao-surface)", line:"var(--ao-line)", line2:"var(--ao-line2)",
  navy:"var(--ao-navy)", teal:"var(--ao-teal)", tealLight:"var(--ao-teal-light)",
  amber:"var(--ao-amber)", amberLight:"var(--ao-amber-light)",
  slate:"var(--ao-slate)", muted:"var(--ao-muted)", text:"var(--ao-text)", onAccent:"#ffffff",
  green:"var(--ao-green)", greenLight:"var(--ao-green-light)",
  shadow:"var(--ao-shadow)",
};
const THEME_CSS = `
  [data-ao-theme="light"]{
    --ao-bg:#f9fafb; --ao-surface:#ffffff; --ao-line:#e5e7eb; --ao-line2:#f3f4f6;
    --ao-navy:#1a2744; --ao-teal:#0099a8; --ao-teal-light:#e6f7f9;
    --ao-amber:#e07b00; --ao-amber-light:#fef3e2;
    --ao-slate:#64748b; --ao-muted:#9ca3af; --ao-text:#111827;
    --ao-green:#16a34a; --ao-green-light:#f0fdf4;
    --ao-shadow: 0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.05);
  }
  [data-ao-theme="dark"]{
    --ao-bg:#0b0f1a; --ao-surface:#121826; --ao-line:#232b3d; --ao-line2:#161d2c;
    --ao-navy:#0d1526; --ao-teal:#22b8c8; --ao-teal-light:#0f2b30;
    --ao-amber:#f2a13a; --ao-amber-light:#2c2110;
    --ao-slate:#94a3b8; --ao-muted:#5b6577; --ao-text:#eef1f6;
    --ao-green:#34d399; --ao-green-light:#0f2a20;
    --ao-shadow: 0 1px 2px rgba(0,0,0,0.3), 0 8px 24px rgba(0,0,0,0.35);
  }
`;

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

function priceDisplay(info) {
  const dollars = (info.price_cents / 100).toFixed(0);
  if (info.interval === "one_time") return `$${dollars} per load`;
  return `+$${dollars}/mo${info.per_unit ? " each" : ""}`;
}

export default function CompanyAddOn() {
  const navigate = useNavigate();
  const user  = (() => { try { return JSON.parse(localStorage.getItem("user")); } catch { return null; } })();
  const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");

  const [theme] = useState(() => { try { return localStorage.getItem("pl_theme") || "light"; } catch { return "light"; } });
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [error, setError] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeNav, setActiveNav] = useState("addons");

  useEffect(() => {
    if (!user || !token) { navigate("/company-login", { replace: true }); return; }
    fetchStatus();
    // eslint-disable-next-line
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/billing/subscription/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setSub(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const purchase = async (kind) => {
    setPurchasing(kind);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/billing/checkout/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ kind }),
      });
      const data = await res.json();
      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError(data.error || "Couldn't start checkout.");
      }
    } catch (err) {
      setError("Network error starting checkout.");
    } finally {
      setPurchasing(null);
    }
  };

  const companyName = user?.company_name || user?.name || "Company";
  const initials = companyName.split(" ").slice(0,2).map(w=>w[0]).join("").toUpperCase() || "CO";

  const ADDON_ORDER = ["advanced_alerts", "extra_seat", "load_posting_fee", "driver_verification_api"];
  const ACTIVE_FIELD = { advanced_alerts: "advanced_alerts", driver_verification_api: "driver_verification_api" };

  return (
    <div data-ao-theme={theme} className="flex h-screen overflow-hidden" style={{ fontFamily:"Inter,system-ui,sans-serif", background:T.bg }}>
      <style dangerouslySetInnerHTML={{__html: THEME_CSS}} />
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={()=>setSidebarOpen(false)}/>}

      <Sidebar activeNav={activeNav} setActiveNav={setActiveNav} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} initials={initials} companyName={companyName}/>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0" style={{ background:T.bg }}>
        <TopBar title="Optional Add-Ons" setSidebarOpen={setSidebarOpen} initials={initials} />

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">

            <div>
              <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2" style={{ color:T.text }}>
                <Flame className="w-5 h-5" style={{ color:T.amber }} /> Optional Add-Ons
              </h1>
              <p className="text-sm mt-1" style={{ color:T.slate }}>
                Every price here is live from the server — what you see is exactly what Stripe will charge.
              </p>
            </div>

            {sub && (
              <div className="rounded-xl p-4 flex items-center gap-3" style={{ background:T.tealLight, border:`1px solid ${T.teal}30` }}>
                <ShieldCheck className="w-5 h-5 flex-shrink-0" style={{ color:T.teal }} />
                <p className="text-xs font-semibold" style={{ color:T.teal }}>
                  {sub.seats_total} dispatcher seat{sub.seats_total === 1 ? "" : "s"} total
                  ({sub.base_seats} included{sub.extra_seats_purchased > 0 ? ` + ${sub.extra_seats_purchased} purchased` : ""})
                </p>
              </div>
            )}

            {error && (
              <div className="rounded-xl p-3 text-xs font-semibold" style={{ background:T.amberLight, color:T.amber, border:`1px solid ${T.amber}30` }}>
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="w-7 h-7 animate-spin" style={{ color:T.teal }} />
                <p className="text-sm font-medium" style={{ color:T.muted }}>Loading your plan…</p>
              </div>
            ) : !sub ? (
              <p className="text-sm" style={{ color:T.muted }}>Couldn't load billing info.</p>
            ) : (
              <div className="rounded-2xl p-5 sm:p-6" style={{ background:T.surface, border:`1px solid ${T.line}`, boxShadow:T.shadow }}>
                <div className="flex items-center gap-2 mb-5">
                  <Flame className="w-4 h-4" style={{ color:T.amber }} />
                  <h2 className="font-bold text-[15px]" style={{ color:T.text }}>Optional Add-Ons</h2>
                </div>

                <div className="space-y-3">
                  {ADDON_ORDER.map(kind => {
                    const info = sub.catalog[kind];
                    if (!info) return null;
                    const activeField = ACTIVE_FIELD[kind];
                    const isActive = activeField ? sub[activeField] : false;
                    const isLoadFee = kind === "load_posting_fee";

                    return (
                      <div key={kind} className="flex items-center justify-between gap-3 p-3 rounded-xl" style={{ border:`1px solid ${T.line}` }}>
                        <div className="min-w-0">
                          <p className="text-sm font-bold" style={{ color:T.text }}>{info.label}</p>
                          <p className="text-xs mt-0.5" style={{ color:T.muted }}>{info.description}</p>
                          <p className="text-xs font-bold mt-1" style={{ color:T.teal }}>{priceDisplay(info)}</p>
                        </div>

                        {isActive ? (
                          <span className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg flex-shrink-0" style={{ background:T.greenLight, color:T.green }}>
                            <CheckCircle2 className="w-3.5 h-3.5" /> Active
                          </span>
                        ) : isLoadFee ? (
                          <span className="text-[11px] flex-shrink-0 text-right max-w-[140px]" style={{ color:T.muted }}>
                            Charged per load at posting time
                          </span>
                        ) : (
                          <button
                            onClick={() => purchase(kind)}
                            disabled={purchasing === kind}
                            className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg flex-shrink-0 disabled:opacity-60"
                            style={{ background:T.navy, color:"#fff" }}
                          >
                            {purchasing === kind ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
                            {purchasing === kind ? "Redirecting…" : "Add"}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </main>
        <BottomNav activeNav={activeNav} setActiveNav={setActiveNav} />
      </div>
    </div>
  );
}
